import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AgeGateContext = createContext(null);

// Cookie/localStorage key
const AGE_VERIFIED_KEY = 'tapcompass_age_verified';
const AGE_VERIFIED_EXPIRY_KEY = 'tapcompass_age_verified_expiry';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function AgeGateProvider({ children }) {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnder21, setIsUnder21] = useState(false);

  // Check if age verification exists and is still valid
  useEffect(() => {
    const checkVerification = () => {
      try {
        const verified = localStorage.getItem(AGE_VERIFIED_KEY);
        const expiry = localStorage.getItem(AGE_VERIFIED_EXPIRY_KEY);
        
        if (verified === 'true' && expiry) {
          const expiryDate = parseInt(expiry, 10);
          if (Date.now() < expiryDate) {
            setIsVerified(true);
          } else {
            // Expired, clear storage
            localStorage.removeItem(AGE_VERIFIED_KEY);
            localStorage.removeItem(AGE_VERIFIED_EXPIRY_KEY);
          }
        }
      } catch (e) {
        console.error('Error checking age verification:', e);
      } finally {
        setIsLoading(false);
      }
    };

    checkVerification();
  }, []);

  // Verify age and store in localStorage for 30 days
  const verifyAge = useCallback((birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    
    // Calculate age
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    if (age >= 21) {
      // Store verification with 30-day expiry
      const expiryDate = Date.now() + THIRTY_DAYS_MS;
      localStorage.setItem(AGE_VERIFIED_KEY, 'true');
      localStorage.setItem(AGE_VERIFIED_EXPIRY_KEY, expiryDate.toString());
      setIsVerified(true);
      setIsUnder21(false);
      return true;
    } else {
      setIsUnder21(true);
      return false;
    }
  }, []);

  // Reset verification (for testing or if user clears)
  const resetVerification = useCallback(() => {
    localStorage.removeItem(AGE_VERIFIED_KEY);
    localStorage.removeItem(AGE_VERIFIED_EXPIRY_KEY);
    setIsVerified(false);
    setIsUnder21(false);
  }, []);

  // Clear under-21 state (to try again)
  const clearUnder21 = useCallback(() => {
    setIsUnder21(false);
  }, []);

  return (
    <AgeGateContext.Provider value={{
      isVerified,
      isLoading,
      isUnder21,
      verifyAge,
      resetVerification,
      clearUnder21
    }}>
      {children}
    </AgeGateContext.Provider>
  );
}

export function useAgeGate() {
  const context = useContext(AgeGateContext);
  if (!context) {
    throw new Error('useAgeGate must be used within an AgeGateProvider');
  }
  return context;
}

export default AgeGateContext;
